import { ManagementSubscriptionDTO } from '@iota/inh-core-v1-management-client';
import { createEntityAdapter, EntityAdapter, EntityState } from '@ngrx/entity';
import { createReducer, createSelector, on } from '@ngrx/store';
import moment from 'moment';
import { subscriptionIsAssociatedToCase } from 'src/app/shared/subscription-utils';
import { subscriptionFitsToRequest, toApiDateString } from 'src/app/shared/utils';
import {
    LoadSubscriptionsByEmployee,
    LoadSubscriptionsFailure,
    LoadSubscriptionsSuccess,
    ResetTransactionSubscription,
    SelectTransactionSubscription,
    ToggleSubscription,
    UpdateSubscriptionsDate,
    UpsertInsuranceNumberFailure
} from '../actions/employee-subscription.actions';
import { compareSubscriptionsByOrder } from '../entity-builder-utils';
import { EmployeeCaseEntity, EmployeeRequestEntity } from '../entity-models';
import { AppState } from '../store';

export interface EmployeeSubscriptionState extends EntityState<ManagementSubscriptionDTO> {
    loading: boolean;
    error: any;
    selectedSubscriptionIds: Map<string, string>;
    subscriptionsDate: string;
    selectedTransactionSubscriptionId: string;
    selectedTransactionSubSubscriptionId: string;

    // Subscription-creation/renewal
    createSubscriptionsLoading: boolean;
    createSubscriptionsError: any;
}

function selectEmployeeSubscriptionId(a: ManagementSubscriptionDTO): string {
    return a.id;
}
export const employeeSubscriptionAdapter: EntityAdapter<ManagementSubscriptionDTO> = createEntityAdapter<ManagementSubscriptionDTO>({
    selectId: selectEmployeeSubscriptionId,
    sortComparer: compareSubscriptionsByOrder
});

export const initialState: EmployeeSubscriptionState = employeeSubscriptionAdapter.getInitialState({
    loading: false,
    error: undefined,
    selectedSubscriptionIds: new Map<string, string>(),
    subscriptionsDate: toApiDateString(new Date()),
    selectedTransactionSubscriptionId: undefined,
    selectedTransactionSubSubscriptionId: undefined,

    createSubscriptionsError: undefined,
    createSubscriptionsLoading: false
});

export const employeeSubscriptionReducer = createReducer(
    initialState,

    on(LoadSubscriptionsByEmployee, (state) => ({
        ...state,
        error: undefined,
        loading: true,
        selectedSubscriptionIds: new Map<string, string>()
    })),

    on(LoadSubscriptionsSuccess, (state, { subscriptions }) =>
        employeeSubscriptionAdapter.upsertMany(subscriptions ?? [], {
            ...state,
            loading: false
        })
    ),

    on(LoadSubscriptionsFailure, UpsertInsuranceNumberFailure, (state, { error }) => ({
        ...state,
        error,
        loading: false
    })),

    on(UpdateSubscriptionsDate, (state, { date }) => ({
        ...state,
        subscriptionsDate: date,
        selectedSubscriptionIds: new Map<string, string>()
    })),
    on(ToggleSubscription, (state, { subscriptionId, subSubscriptionId }) => {
        const selectedSubscriptionIdsCopy = new Map<string, string>(state.selectedSubscriptionIds);

        if (selectedSubscriptionIdsCopy.has(subscriptionId) && selectedSubscriptionIdsCopy.get(subscriptionId) === subSubscriptionId) {
            selectedSubscriptionIdsCopy.delete(subscriptionId);
        } else {
            selectedSubscriptionIdsCopy.set(subscriptionId, subSubscriptionId);
        }

        return { ...state, selectedSubscriptionIds: selectedSubscriptionIdsCopy };
    }),

    on(SelectTransactionSubscription, (state, { subscriptionId, subSubscriptionId }) => ({
        ...state,
        selectedTransactionSubscriptionId: subscriptionId,
        selectedTransactionSubSubscriptionId: subSubscriptionId
    })),
    on(ResetTransactionSubscription, (state) => ({
        ...state,
        selectedTransactionSubscriptionId: undefined,
        selectedTransactionSubSubscriptionId: undefined
    }))
);

const { selectAll, selectIds } = employeeSubscriptionAdapter.getSelectors();

export const employeSubscriptionState = (state: AppState) => state.employeeSubscriptionState;
export const employeeSubscriptions = createSelector(employeSubscriptionState, selectAll);
export const loadedEmployeeSubscriptions = createSelector(employeSubscriptionState, selectIds);
export const employeeSubscriptionsLoading = createSelector(employeSubscriptionState, ({ loading }) => loading);
export const employeeSubscriptionsError = createSelector(employeSubscriptionState, ({ error }) => error);
export const employeeSubscriptionsDate = createSelector(employeSubscriptionState, ({ subscriptionsDate }) => subscriptionsDate);
export const selectedSubscriptionIds = createSelector(employeSubscriptionState, (state) =>
    Array.from(state.selectedSubscriptionIds.keys())
);
export const selectedSubSubscriptionIds = createSelector(employeSubscriptionState, (state) =>
    Array.from(state.selectedSubscriptionIds.values()).filter((id): id is string => id != null)
);
export const selectedTransactionSubscriptionId = createSelector(
    employeSubscriptionState,
    (state) => state.selectedTransactionSubscriptionId
);
export const selectedTransactionSubscription = createSelector(employeeSubscriptions, selectedTransactionSubscriptionId, (subs, id) =>
    subs.find((sub) => sub.id === id)
);
export const selectedTransactionSubSubscriptionId = createSelector(
    employeSubscriptionState,
    (state) => state.selectedTransactionSubSubscriptionId
);

export const subscriptionById = (subscriptionId: string) =>
    createSelector(employeeSubscriptions, (subscriptions) => subscriptions.find((sub) => sub.id === subscriptionId));

export const subscriptionsByCase = (employeeCase: EmployeeCaseEntity) =>
    createSelector(employeeSubscriptions, (subscriptions) =>
        subscriptions.filter((subscription) => subscriptionIsAssociatedToCase(subscription, employeeCase))
    );

export const possibleSubscriptionsForRequest = (employeeRequest: EmployeeRequestEntity) =>
    createSelector(employeeSubscriptions, employeeSubscriptionsDate, (subscriptions, date) => {
        return subscriptions.filter(
            (subscription) =>
                subscription.employeeId === employeeRequest.employeeId &&
                subscriptionFitsToRequest(subscription, employeeRequest) &&
                // Subscriptions without a remaining budget, including those without a usage limit, are not assignable.
                subscription.budget?.remainingBudgetIncludingPending > 0 &&
                moment.utc(date).isBetween(moment.utc(subscription.budgetFrom), moment.utc(subscription.budgetTo), 'day', '[]')
        );
    });

export const subscriptionsByEmployeeAndSelectedDate = (employeeId: string) =>
    createSelector(employeeSubscriptions, employeeSubscriptionsDate, (subscriptions, date) => {
        return subscriptions.filter(
            (subscription) =>
                moment.utc(date).isBetween(moment.utc(subscription.budgetFrom), moment.utc(subscription.budgetTo), 'day', '[]') &&
                subscription.employeeId === employeeId
        );
    });

export const subscriptionsByEmployee = (employeeId: string) =>
    createSelector(employeeSubscriptions, (subscriptions) =>
        subscriptions.filter((subscription) => subscription.employeeId === employeeId)
    );

export const createSubscriptionsLoading = createSelector(employeSubscriptionState, (state) => state?.createSubscriptionsLoading);
export const createSubscriptionsError = createSelector(employeSubscriptionState, (state) => state?.createSubscriptionsError);
